'use client'

import { forwardRef, ReactNode, useState, useCallback, useMemo } from 'react'
import { Search, X, Filter, RotateCcw } from 'lucide-react'
import { cn } from '../utils/cn'
import { BirhausButton } from './BirhausButton'
import { BirhausQuickFilter, type BirhausQuickFilterProps } from './BirhausQuickFilter'

/**
 * BIRHAUS Filter Bar Component
 * 
 * Comprehensive filtering interface implementing BIRHAUS principles:
 * - Progressive disclosure (search -> quick filters -> advanced filters)
 * - Spanish-first labels and messages
 * - Miller's Law compliance (max 7±2 quick filters)
 * - 4-3-1 cognitive rule for filter organization
 * - Clear visual feedback for active filters
 * - Accessibility compliance (WCAG AA+)
 * - Undo-over-confirm patterns
 */

export interface FilterConfig {
  id: string
  labelEs: string
  labelEn?: string
  type: 'select' | 'multiselect' | 'date' | 'daterange' | 'number' | 'text'
  options?: Array<{ value: string; labelEs: string; labelEn?: string }>
  value?: any
  onChange: (value: any) => void
  placeholder?: string
  placeholderEs?: string
  placeholderEn?: string
}

export interface BirhausQuickFilterConfig extends Omit<BirhausQuickFilterProps, 'onClick'> {
  key: string
  onClick?: () => void | Promise<void>
}

export interface BirhausFilterBarProps {
  // BIRHAUS Principle #4: Progressive disclosure - Search first
  search?: {
    value: string
    onChange: (value: string) => void
    placeholder?: string
    placeholderEs?: string
    placeholderEn?: string
    debounceMs?: number
  }
  
  // Quick filters (BIRHAUS 4-3-1 rule: max 4 primary quick filters)
  quickFilters?: BirhausQuickFilterConfig[]
  
  // Advanced filters (progressive disclosure)
  advancedFilters?: FilterConfig[]
  showAdvancedFilters?: boolean
  onToggleAdvancedFilters?: (show: boolean) => void
  
  // Custom filter content (for complex scenarios)
  customFilters?: ReactNode
  
  // Results information
  results?: {
    count: number
    labelEs?: string
    labelEn?: string
    totalCount?: number
  }
  
  // Actions
  onClearAll?: () => void | Promise<void>
  onExport?: () => void | Promise<void>
  
  // BIRHAUS Principle #7: Bilingual labels
  clearAllLabelEs?: string
  clearAllLabelEn?: string
  advancedFiltersLabelEs?: string
  advancedFiltersLabelEn?: string
  quickFiltersLabelEs?: string
  quickFiltersLabelEn?: string
  showingLabelEs?: string
  showingLabelEn?: string
  
  // State
  loading?: boolean
  
  // Locale
  locale?: 'es' | 'en'
  
  // Styling
  variant?: 'default' | 'minimal' | 'card'
  className?: string
  children?: ReactNode
}

export const BirhausFilterBar = forwardRef<HTMLDivElement, BirhausFilterBarProps>(({
  // Search
  search,
  
  // Filters
  quickFilters = [],
  advancedFilters = [],
  showAdvancedFilters = false,
  onToggleAdvancedFilters,
  customFilters,
  
  // Results
  results,
  
  // Actions
  onClearAll,
  onExport,
  
  // Labels
  clearAllLabelEs = 'Limpiar filtros',
  clearAllLabelEn = 'Clear filters',
  advancedFiltersLabelEs = 'Filtros avanzados',
  advancedFiltersLabelEn = 'Advanced filters',
  quickFiltersLabelEs = 'Filtros rápidos:',
  quickFiltersLabelEn = 'Quick filters:',
  showingLabelEs = 'Mostrando',
  showingLabelEn = 'Showing',
  
  // State
  loading = false,
  
  // Locale
  locale = 'es',
  
  // Styling
  variant = 'default',
  className,
  children,
  
  ...props
}, ref) => {
  // Local state for search debouncing
  const [searchValue, setSearchValue] = useState(search?.value || '')
  
  // BIRHAUS Principle #7: Spanish-first text resolution
  const getLabel = useCallback((es: string, en?: string) => {
    return locale === 'es' ? es : (en || es)
  }, [locale])
  
  // BIRHAUS Validation: Check Miller's Law compliance for quick filters
  const quickFiltersCount = quickFilters.length
  if (quickFiltersCount > 7) {
    console.warn(
      `BIRHAUS FilterBar: ${quickFiltersCount} quick filters exceed Miller's Law recommendation of 7. ` +
      'Consider grouping filters or using advanced filters section.'
    )
  }
  
  // Check for active filters
  const hasActiveQuickFilters = quickFilters.some(filter => filter.active)
  const hasSearchTerm = Boolean(search?.value)
  const hasActiveFilters = hasActiveQuickFilters || hasSearchTerm || showAdvancedFilters
  
  // Handle search with optional debouncing
  const handleSearchChange = useCallback((value: string) => {
    setSearchValue(value)
    if (search?.debounceMs) {
      // Implement debouncing if specified
      const timeoutId = setTimeout(() => {
        search.onChange(value)
      }, search.debounceMs)
      return () => clearTimeout(timeoutId)
    } else {
      search?.onChange(value)
    }
  }, [search])
  
  // Clear search
  const handleClearSearch = useCallback(() => {
    setSearchValue('')
    search?.onChange('')
  }, [search])
  
  // Handle clear all filters
  const handleClearAll = useCallback(async () => {
    // Clear search
    if (search?.value) {
      handleClearSearch()
    }
    
    // Clear quick filters by calling their onClick handlers if they're active
    quickFilters.forEach(filter => {
      if (filter.active && filter.onClick) {
        filter.onClick()
      }
    })
    
    // Hide advanced filters
    if (showAdvancedFilters) {
      onToggleAdvancedFilters?.(false)
    }
    
    // Call custom clear handler
    await onClearAll?.()
  }, [search, quickFilters, showAdvancedFilters, onToggleAdvancedFilters, onClearAll, handleClearSearch])
  
  // Variant styles
  const variantClasses = {
    default: 'bg-white rounded-xl shadow-sm border border-gray-100 p-6',
    minimal: 'bg-transparent border-b border-gray-200 pb-4',
    card: 'bg-gray-50 rounded-lg border border-gray-200 p-4'
  }
  
  return (
    <div
      ref={ref}
      className={cn(
        'birhaus-filter-bar',
        variantClasses[variant],
        className
      )}
      role="search"
      aria-label={getLabel('Barra de filtros', 'Filter bar')}
      {...props}
    >
      <div className="space-y-4">
        {/* Main Filter Controls Row */}
        <div className="flex flex-col lg:flex-row lg:items-center gap-4">
          {/* Search Input */}
          {search && (
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder={getLabel(
                  search.placeholderEs || 'Buscar...', 
                  search.placeholderEn || 'Search...'
                )}
                value={searchValue}
                onChange={(e) => handleSearchChange(e.target.value)}
                disabled={loading}
                className={cn(
                  'w-full pl-10 pr-10 py-2.5 border rounded-lg transition-all duration-200',
                  'focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent',
                  'disabled:opacity-50 disabled:cursor-not-allowed',
                  'border-gray-200 bg-white text-gray-900 placeholder:text-gray-400'
                )}
                aria-label={getLabel(
                  search.placeholderEs || 'Buscar elementos',
                  search.placeholderEn || 'Search items'
                )}
              />
              {searchValue && (
                <button
                  onClick={handleClearSearch}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                  aria-label={getLabel('Limpiar búsqueda', 'Clear search')}
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
          )}
          
          {/* Custom Filters */}
          {customFilters}
          
          {/* Advanced Filters Toggle */}
          {advancedFilters.length > 0 && (
            <BirhausButton
              variant="ghost"
              size="sm"
              onClick={() => onToggleAdvancedFilters?.(!showAdvancedFilters)}
              disabled={loading}
            >
              <Filter className="w-4 h-4 mr-2" />
              {getLabel(advancedFiltersLabelEs, advancedFiltersLabelEn)}
            </BirhausButton>
          )}
          
          {/* Export Button */}
          {onExport && (
            <BirhausButton
              variant="secondary"
              size="sm"
              onClick={onExport}
              disabled={loading}
            >
              {getLabel('Exportar', 'Export')}
            </BirhausButton>
          )}
          
          {/* Additional Controls */}
          {children}
        </div>
        
        {/* Quick Filters Row */}
        {quickFilters.length > 0 && (
          <div className="flex items-center flex-wrap gap-4">
            <span className="text-sm text-gray-600 font-medium">
              {getLabel(quickFiltersLabelEs, quickFiltersLabelEn)}
            </span>
            <div className="flex flex-wrap gap-2">
              {quickFilters.map((filter) => {
                const { key, ...filterProps } = filter
                return (
                  <BirhausQuickFilter
                    key={key}
                    {...filterProps}
                    locale={locale}
                    disabled={loading || filter.disabled}
                  />
                )
              })}
            </div>
          </div>
        )}
        
        {/* Advanced Filters Section (Progressive Disclosure) */}
        {showAdvancedFilters && advancedFilters.length > 0 && (
          <div className="border-t border-gray-200 pt-4">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {advancedFilters.map((filter) => (
                <div key={filter.id} className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">
                    {getLabel(filter.labelEs, filter.labelEn)}
                  </label>
                  {filter.type === 'select' && (
                    <select
                      value={filter.value || ''}
                      onChange={(e) => filter.onChange(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      disabled={loading}
                    >
                      <option value="">
                        {getLabel(
                          filter.placeholderEs || 'Seleccionar...',
                          filter.placeholderEn || 'Select...'
                        )}
                      </option>
                      {filter.options?.map(option => (
                        <option key={option.value} value={option.value}>
                          {getLabel(option.labelEs, option.labelEn)}
                        </option>
                      ))}
                    </select>
                  )}
                  {/* Add other filter types as needed */}
                </div>
              ))}
            </div>
          </div>
        )}
        
        {/* Results Summary and Clear Actions */}
        <div className="flex items-center justify-between pt-2 border-t border-gray-100">
          {/* Results Count */}
          {results && (
            <div className="flex items-center gap-2">
              <p className="text-sm text-gray-600">
                {getLabel(
                  results.labelEs || showingLabelEs,
                  results.labelEn || showingLabelEn
                )}{' '}
                <span className="font-semibold text-gray-900">
                  {results.count.toLocaleString()}
                </span>
                {results.totalCount && results.totalCount !== results.count && (
                  <span className="text-gray-500">
                    {' '}/{' '}{results.totalCount.toLocaleString()}
                  </span>
                )}{' '}
                {getLabel('resultados', 'results')}
              </p>
              {loading && (
                <div className="animate-spin w-4 h-4 border-2 border-gray-200 border-t-blue-600 rounded-full" />
              )}
            </div>
          )}
          
          {/* Clear All Button */}
          {hasActiveFilters && onClearAll && (
            <BirhausButton
              variant="ghost"
              size="sm"
              onClick={handleClearAll}
              disabled={loading}
              className="text-blue-600 hover:text-blue-800"
            >
              <RotateCcw className="w-4 h-4 mr-1" />
              {getLabel(clearAllLabelEs, clearAllLabelEn)}
            </BirhausButton>
          )}
        </div>
      </div>
    </div>
  )
})

BirhausFilterBar.displayName = 'BirhausFilterBar'

// Hook for managing filter bar state
export function useBirhausFilterBar(initialFilters: Record<string, any> = {}) {
  const [filters, setFilters] = useState(initialFilters)
  const [searchTerm, setSearchTerm] = useState('')
  const [showAdvanced, setShowAdvanced] = useState(false)
  
  const updateFilter = useCallback((key: string, value: any) => {
    setFilters(prev => ({ ...prev, [key]: value }))
  }, [])
  
  const clearFilter = useCallback((key: string) => {
    setFilters(prev => {
      const updated = { ...prev }
      delete updated[key]
      return updated
    })
  }, [])
  
  const clearAllFilters = useCallback(() => {
    setFilters({})
    setSearchTerm('')
    setShowAdvanced(false)
  }, [])
  
  const activeFilters = useMemo(() => {
    return Object.entries(filters).filter(([_, value]) => {
      if (Array.isArray(value)) return value.length > 0
      return value !== null && value !== undefined && value !== ''
    })
  }, [filters])
  
  const hasActiveFilters = useMemo(() => {
    return searchTerm !== '' || activeFilters.length > 0
  }, [searchTerm, activeFilters])
  
  return {
    // State
    filters,
    searchTerm,
    showAdvanced,
    
    // Setters
    setFilters,
    setSearchTerm,
    setShowAdvanced,
    
    // Methods
    updateFilter,
    clearFilter,
    clearAllFilters,
    
    // Computed
    activeFilters,
    hasActiveFilters,
    activeFiltersCount: activeFilters.length
  }
}

// Common filter configurations for BIRHAUS applications
export const birhausFilterBarPresets = {
  // Student management filters
  studentManagement: {
    quickFilters: [
      { key: 'active', labelEs: 'Activos', labelEn: 'Active', icon: '👤' },
      { key: 'pending', labelEs: 'Pendientes', labelEn: 'Pending', icon: '⏳' },
      { key: 'graduated', labelEs: 'Graduados', labelEn: 'Graduated', icon: '🎓' }
    ],
    advancedFilters: [
      {
        id: 'course',
        labelEs: 'Curso',
        labelEn: 'Course',
        type: 'select' as const,
        options: []
      },
      {
        id: 'enrollmentDate',
        labelEs: 'Fecha de Matrícula',
        labelEn: 'Enrollment Date',
        type: 'daterange' as const
      }
    ]
  },
  
  // Financial management filters
  financialManagement: {
    quickFilters: [
      { key: 'paid', labelEs: 'Pagado', labelEn: 'Paid', icon: '✅' },
      { key: 'pending', labelEs: 'Pendiente', labelEn: 'Pending', icon: '⏳' },
      { key: 'overdue', labelEs: 'Vencido', labelEn: 'Overdue', icon: '⚠️' }
    ],
    advancedFilters: [
      {
        id: 'amount',
        labelEs: 'Monto',
        labelEn: 'Amount',
        type: 'number' as const
      },
      {
        id: 'paymentMethod',
        labelEs: 'Método de Pago',
        labelEn: 'Payment Method',
        type: 'select' as const,
        options: []
      }
    ]
  }
}